import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../i18n';
import { formatLocalTime, isTodayInTz } from '../utils/time';
import analytics from '../lib/analytics';

/**
 * Banner that renders above the Quick-Actions section on HomePage.
 *
 * For Bob (claimer): pending requests on his scheduled session →
 *   [Accept change] [Cancel my claim]
 *
 * For Alice (owner): accepted-but-not-yet-dismissed requests she filed →
 *   "Bob accepted your change" + [Dismiss]
 *
 * Cancel-claim reuses the existing `POST /sessions/:id/end` endpoint;
 * no new server code path was added for it.
 */

interface ChangeRequestRow {
  id: string;
  spot_id: string;
  window_id: string;
  session_id: string;
  building_id: string;
  owner_id: string;
  claimer_id: string;
  proposed_starts_at: string | null;
  proposed_ends_at: string | null;
  owner_note: string | null;
  status: 'pending' | 'accepted' | 'rejected' | 'dismissed' | 'superseded';
  created_at: string;
  resolved_at: string | null;
  spot_number: string;
  level_code: string;
  level_label: string;
  owner_apartment: string;
  claimer_apartment: string;
  current_window_starts_at: string;
  current_window_ends_at: string;
  role: 'claimer' | 'owner';
}

function fmtRange(starts: string | null, ends: string | null, timezone: string, language: 'en' | 'he'): string {
  if (!starts || !ends) return '';
  const startStr = isTodayInTz(starts, timezone)
    ? formatLocalTime(starts, timezone, 'time', language)
    : formatLocalTime(starts, timezone, 'datetime-weekday', language);
  const endStr = isTodayInTz(ends, timezone)
    ? formatLocalTime(ends, timezone, 'time', language)
    : formatLocalTime(ends, timezone, 'datetime-weekday', language);
  return `${startStr} → ${endStr}`;
}

export function AvailabilityChangeAlert() {
  const { t, language } = useTranslation();
  const qc = useQueryClient();
  const timezone = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');

  const requestsQ = useQuery({
    queryKey: ['availability-change-requests', 'mine'],
    queryFn: () =>
      apiClient
        .get<{ data: ChangeRequestRow[] }>('/availability-change-requests/mine')
        .then((r) => r.data.data),
    // Same default staleTime as other real-time queries; WS pushes invalidate.
    staleTime: 30_000,
  });

  const acceptMutation = useMutation({
    mutationFn: (args: { id: string; spotId: string }) =>
      apiClient.post(`/availability-change-requests/${args.id}/accept`),
    onSuccess: (_res, args) => {
      analytics.trackAvailabilityChangeAccepted(args.spotId);
      void qc.invalidateQueries({ queryKey: ['availability-change-requests', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['spots', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['session', 'active'] });
      void qc.invalidateQueries({ queryKey: ['sessions', 'scheduled'] });
    },
  });

  const cancelClaimMutation = useMutation({
    mutationFn: (args: { sessionId: string; spotId: string }) =>
      apiClient.post(`/sessions/${args.sessionId}/end`),
    onSuccess: (_res, args) => {
      // Distinguish "cancelled from the negotiation banner" from a generic
      // session end so we can measure how many claimers choose to walk away
      // when an owner asks for a change.
      analytics.trackAvailabilityChangeClaimCancelled(args.spotId);
      void qc.invalidateQueries({ queryKey: ['availability-change-requests', 'mine'] });
      void qc.invalidateQueries({ queryKey: ['spots', 'free'] });
      void qc.invalidateQueries({ queryKey: ['session', 'active'] });
      void qc.invalidateQueries({ queryKey: ['sessions', 'scheduled'] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (args: { id: string; spotId: string }) =>
      apiClient.post(`/availability-change-requests/${args.id}/dismiss`),
    onSuccess: (_res, args) => {
      analytics.trackAvailabilityChangeDismissed(args.spotId);
      void qc.invalidateQueries({ queryKey: ['availability-change-requests', 'mine'] });
    },
  });

  const rows = requestsQ.data ?? [];
  if (rows.length === 0) return null;

  return (
    <section style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {rows.map((r) => {
        const spotLabel = `${t(r.level_code) || r.level_label} · ${r.spot_number}`;
        const currentRange = fmtRange(r.current_window_starts_at, r.current_window_ends_at, timezone, language);
        const proposedRange = fmtRange(r.proposed_starts_at, r.proposed_ends_at, timezone, language);

        if (r.role === 'claimer') {
          return (
            <div
              key={r.id}
              style={{
                background: '#fff7ed',
                border: '1.5px solid #fdba74',
                borderRadius: '16px',
                padding: '16px 18px',
                boxShadow: '0 4px 12px rgba(253,186,116,0.15)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span className="material-symbols-outlined" style={{ color: '#c2410c' }}>edit_calendar</span>
                <div style={{ fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '15px', color: '#9a3412' }}>
                  {t('availability_change_title')}
                </div>
              </div>
              <div style={{ fontSize: '13px', color: '#7c2d12', lineHeight: 1.45, marginBottom: '4px' }}>
                {t('availability_change_owner_apt').replace('{apt}', r.owner_apartment)} {spotLabel}
              </div>
              <div style={{ fontSize: '13px', color: '#7c2d12', marginBottom: '4px' }}>
                {t('availability_change_was')}: <strong>{currentRange}</strong>
              </div>
              <div style={{ fontSize: '13px', color: '#7c2d12', marginBottom: '12px' }}>
                {t('availability_change_proposed')}: <strong>{proposedRange}</strong>
              </div>
              {r.owner_note && (
                <div style={{
                  fontSize: '12px',
                  color: '#7c2d12',
                  fontStyle: 'italic',
                  marginBottom: '12px',
                  paddingInlineStart: '8px',
                  borderInlineStart: '2px solid #fdba74',
                }}>
                  "{r.owner_note}"
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => acceptMutation.mutate({ id: r.id, spotId: r.spot_id })}
                  disabled={acceptMutation.isPending}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#16a34a',
                    color: '#ffffff',
                    fontFamily: 'var(--manrope)',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {acceptMutation.isPending ? '…' : t('availability_change_accept')}
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(t('availability_change_cancel_confirm'))) {
                      cancelClaimMutation.mutate({ sessionId: r.session_id, spotId: r.spot_id });
                    }
                  }}
                  disabled={cancelClaimMutation.isPending}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #c2410c',
                    background: '#ffffff',
                    color: '#c2410c',
                    fontFamily: 'var(--manrope)',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {cancelClaimMutation.isPending ? '…' : t('availability_change_cancel_my_claim')}
                </button>
              </div>
            </div>
          );
        }

        // role === 'owner' → "accepted" notice
        return (
          <div
            key={r.id}
            style={{
              background: '#ecfdf5',
              border: '1.5px solid #6ee7b7',
              borderRadius: '16px',
              padding: '16px 18px',
              boxShadow: '0 4px 12px rgba(110,231,183,0.15)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span className="material-symbols-outlined" style={{ color: '#047857' }}>event_available</span>
              <div style={{ fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '15px', color: '#065f46' }}>
                {t('availability_change_accepted_title')}
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '12px' }}>
              {t('availability_change_accepted_body')
                .replace('{apt}', r.claimer_apartment)
                .replace('{spot}', spotLabel)}
            </div>
            <button
              onClick={() => dismissMutation.mutate({ id: r.id, spotId: r.spot_id })}
              disabled={dismissMutation.isPending}
              style={{
                padding: '8px 14px',
                borderRadius: '10px',
                border: '1px solid #047857',
                background: '#ffffff',
                color: '#047857',
                fontFamily: 'var(--manrope)',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              {dismissMutation.isPending ? '…' : t('availability_change_dismiss')}
            </button>
          </div>
        );
      })}
    </section>
  );
}
